import React, { useState, useEffect } from 'react';
import { X, Save } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Select } from '../ui/select';
import { RichTextEditor } from '../ui/RichTextEditor';
import {
  Ticket,
  CreateTicketRequest,
  UpdateTicketRequest,
  TicketType,
  TicketPriority,
  TicketArea,
  TicketStatus,
  ticketPriorityLabels,
  ticketAreaLabels,
  ticketStatusLabels
} from '../../types/ticket';
import { getTeamMembers } from '../../services/teamApi';
import { getServiceOrders } from '../../services/api';

interface TicketFormProps {
  projectId: string;
  ticket?: Ticket;
  serviceOrderId?: string;
  onSubmit: (data: CreateTicketRequest | UpdateTicketRequest) => Promise<void>;
  onClose: () => void;
}

interface ServiceOrder {
  _id: string;
  name: string;
  code: string;
}

interface TeamMember {
  _id: string;
  userId: { _id: string; name: string; email: string };
  role: string;
}

export const TicketForm: React.FC<TicketFormProps> = ({ projectId, ticket, serviceOrderId, onSubmit, onClose }) => {
  const [formData, setFormData] = useState({
    titulo: ticket?.titulo || '',
    descricao: ticket?.descricao || '',
    tipo: ticket?.tipo || 'bug' as TicketType,
    prioridade: ticket?.prioridade || 'media' as TicketPriority,
    area: ticket?.area || 'backend' as TicketArea,
    status: ticket?.status || 'aberto' as TicketStatus,
    estimatedHours: ticket?.estimatedHours || 0,
    serviceOrderId: serviceOrderId || (typeof ticket?.serviceOrderId === 'object' ? ticket?.serviceOrderId?._id : ticket?.serviceOrderId) || '',
    responsavelId: (typeof ticket?.responsavelId === 'object' ? ticket?.responsavelId?._id : ticket?.responsavelId) || ''
  });

  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [serviceOrders, setServiceOrders] = useState<ServiceOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    const loadData = async () => {
      try {
        const [teamData, ordersResponse] = await Promise.all([
          getTeamMembers(projectId),
          getServiceOrders(projectId)
        ]);
        setTeamMembers(teamData.members);
        setServiceOrders(ordersResponse.data.data.serviceOrders || []);
      } catch (err) {
        console.error('Error loading form data:', err);
      }
    };
    loadData();
  }, [projectId]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.titulo.trim()) {
      newErrors.titulo = 'Título é obrigatório';
    } else if (formData.titulo.length > 200) {
      newErrors.titulo = 'Título deve ter no máximo 200 caracteres';
    }

    if (!formData.descricao.trim()) {
      newErrors.descricao = 'Descrição é obrigatória';
    } else if (formData.descricao.length > 5000) {
      newErrors.descricao = 'Descrição deve ter no máximo 5000 caracteres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const data: CreateTicketRequest | UpdateTicketRequest = {
        titulo: formData.titulo,
        descricao: formData.descricao,
        tipo: formData.tipo,
        prioridade: formData.prioridade,
        area: formData.area,
        estimatedHours: formData.estimatedHours || undefined,
        serviceOrderId: formData.serviceOrderId || undefined,
        responsavelId: formData.responsavelId || undefined
      };

      if (ticket) {
        (data as UpdateTicketRequest).status = formData.status;
      }

      await onSubmit(data);
      onClose();
    } catch (err: any) {
      setErrors({ form: err.response?.data?.message || 'Erro ao salvar chamado' });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const ticketTypeOptions = [
    { value: 'bug', label: 'Bug' },
    { value: 'melhoria', label: 'Melhoria' }
  ];

  const serviceOrderOptions = [
    { value: '', label: 'Nenhuma' },
    ...serviceOrders.map(so => ({ value: so._id, label: `${so.code} - ${so.name}` }))
  ];

  const responsavelOptions = [
    { value: '', label: 'Não atribuído' },
    ...teamMembers.map(m => ({ value: m.userId._id, label: m.userId.name }))
  ];

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <Card variant="glass" className="w-full max-w-2xl max-h-[90vh] overflow-auto">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-xl text-foreground-primary">
            {ticket ? 'Editar Chamado' : 'Novo Chamado'}
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            disabled={loading}
            className="text-foreground-tertiary hover:text-foreground-primary"
          >
            <X className="w-4 h-4" />
          </Button>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {errors.form && (
              <div className="p-3 bg-red-500/10 border border-red-500/20 rounded text-red-400 text-sm">
                {errors.form}
              </div>
            )}

            {/* Título */}
            <div className="space-y-2">
              <Label htmlFor="titulo" className="text-foreground-secondary">
                Título *
              </Label>
              <Input
                id="titulo"
                value={formData.titulo}
                onChange={(e) => handleInputChange('titulo', e.target.value)}
                placeholder="Digite o título do chamado"
                className={errors.titulo ? 'border-red-500' : ''}
                disabled={loading}
              />
              {errors.titulo && (
                <p className="text-sm text-red-400">{errors.titulo}</p>
              )}
            </div>

            {/* Descrição */}
            <div className="space-y-2">
              <Label htmlFor="descricao" className="text-foreground-secondary">
                Descrição *
              </Label>
              <RichTextEditor
                id="descricao"
                value={formData.descricao}
                onChange={(value) => handleInputChange('descricao', value)}
                placeholder="Descreva o chamado"
                className={errors.descricao ? 'border-red-500' : ''}
                disabled={loading}
              />
              {errors.descricao && (
                <p className="text-sm text-red-400">{errors.descricao}</p>
              )}
            </div>

            {/* Grid: Tipo + Prioridade */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="tipo" className="text-foreground-secondary">
                  Tipo *
                </Label>
                <Select
                  id="tipo"
                  value={formData.tipo}
                  onChange={(value) => handleInputChange('tipo', value as TicketType)}
                  options={ticketTypeOptions}
                  disabled={loading}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="prioridade" className="text-foreground-secondary">
                  Prioridade *
                </Label>
                <Select
                  id="prioridade"
                  value={formData.prioridade}
                  onChange={(value) => handleInputChange('prioridade', value as TicketPriority)}
                  options={Object.entries(ticketPriorityLabels).map(([value, label]) => ({
                    value,
                    label
                  }))}
                  disabled={loading}
                />
              </div>
            </div>

            {/* Grid: Área + Horas Estimadas */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="area" className="text-foreground-secondary">
                  Área *
                </Label>
                <Select
                  id="area"
                  value={formData.area}
                  onChange={(value) => handleInputChange('area', value as TicketArea)}
                  options={Object.entries(ticketAreaLabels).map(([value, label]) => ({
                    value,
                    label
                  }))}
                  disabled={loading}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="estimatedHours" className="text-foreground-secondary">
                  Horas Estimadas
                </Label>
                <Input
                  id="estimatedHours"
                  type="number"
                  min="0"
                  max="200"
                  step="0.5"
                  value={formData.estimatedHours || ''}
                  onChange={(e) => handleInputChange('estimatedHours', e.target.value ? parseFloat(e.target.value) : 0)}
                  disabled={loading}
                />
              </div>
            </div>

            {/* Grid: OS + Responsável */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="serviceOrderId" className="text-foreground-secondary">
                  Ordem de Serviço
                </Label>
                <Select
                  id="serviceOrderId"
                  value={formData.serviceOrderId}
                  onChange={(value) => handleInputChange('serviceOrderId', value)}
                  options={serviceOrderOptions}
                  disabled={loading || !!serviceOrderId}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="responsavelId" className="text-foreground-secondary">
                  Responsável
                </Label>
                <Select
                  id="responsavelId"
                  value={formData.responsavelId}
                  onChange={(value) => handleInputChange('responsavelId', value)}
                  options={responsavelOptions}
                  disabled={loading}
                />
              </div>
            </div>

            {/* Status (edit only) */}
            {ticket && (
              <div className="space-y-2">
                <Label htmlFor="status" className="text-foreground-secondary">
                  Status
                </Label>
                <Select
                  id="status"
                  value={formData.status}
                  onChange={(value) => handleInputChange('status', value as TicketStatus)}
                  options={Object.entries(ticketStatusLabels).map(([value, label]) => ({
                    value,
                    label
                  }))}
                  disabled={loading}
                />
              </div>
            )}

            {/* Botões */}
            <div className="flex gap-3 pt-4">
              <Button
                type="submit"
                variant="gradient"
                loading={loading}
                loadingText="Salvando..."
                className="flex-1"
              >
                <Save className="w-4 h-4 mr-2" />
                {ticket ? 'Atualizar' : 'Criar'} Chamado
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={onClose}
                disabled={loading}
              >
                Cancelar
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
